import { useEffect, useState } from "react";
import { isIos, isStandalone } from "@/lib/pwa";
import { currentSubscription, disablePush, enablePush, pushSupported, sendTestPush } from "@/lib/push";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Check, Download, Share, Smartphone } from "lucide-react";
import { toast } from "sonner";

function Step({ n, children }) {
  return (
    <li className="flex gap-2.5">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">{n}</span>
      <span className="text-sm text-muted-foreground">{children}</span>
    </li>
  );
}

/**
 * Onde a equipa instala o CRM no telemóvel e liga as notificações.
 * Fica no Perfil para haver sempre um sítio fixo a que voltar — o banner
 * automático só aparece no telemóvel e pode ser dispensado.
 */
export function AppAndNotifications() {
  const [deferredInstall, setDeferredInstall] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const installed = isStandalone();
  const ios = isIos();
  const supported = pushSupported();

  useEffect(() => {
    const onPrompt = (event) => { event.preventDefault(); setDeferredInstall(event); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    currentSubscription().then((sub) => setSubscribed(Boolean(sub))).catch(() => undefined);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferredInstall) return;
    deferredInstall.prompt();
    await deferredInstall.userChoice;
    setDeferredInstall(null);
  };

  const toggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await disablePush();
        setSubscribed(false);
        toast.success("Notificações desligadas neste dispositivo");
      } else {
        await enablePush();
        setSubscribed(true);
        toast.success("Notificações ligadas neste dispositivo");
      }
    } catch (e) {
      toast.error(e.message || "Não foi possível alterar as notificações");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    try {
      await sendTestPush();
      toast.success("Enviada. Deve aparecer daqui a instantes.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível enviar o teste");
    }
  };

  return (
    <Card className="mt-6 p-6">
      <div className="mb-1 flex items-center gap-2">
        <Smartphone className="h-5 w-5 text-primary" />
        <h2 className="font-poppins text-lg font-semibold">App no telemóvel e notificações</h2>
      </div>
      <p className="mb-5 text-sm text-muted-foreground">
        O CRM instala-se como app a partir do browser — não há nada para descarregar de nenhuma loja.
      </p>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-border p-4">
          <h3 className="mb-2 flex items-center gap-2 font-poppins text-sm font-semibold">
            <Download className="h-4 w-4 text-primary" /> Instalar a app
          </h3>
          {installed ? (
            <p className="flex items-center gap-1.5 text-sm text-emerald-600"><Check className="h-4 w-4" />Já estás a usar a app instalada.</p>
          ) : ios ? (
            <ol className="space-y-2">
              <Step n="1">Abre este endereço no <b>Safari</b> (no iPhone só o Safari consegue instalar).</Step>
              <Step n="2">Toca em <Share className="inline h-3.5 w-3.5" /> <b>Partilhar</b>, na barra de baixo.</Step>
              <Step n="3">Escolhe <b>Adicionar ao ecrã principal</b>.</Step>
            </ol>
          ) : deferredInstall ? (
            <>
              <p className="mb-3 text-sm text-muted-foreground">O teu browser pode instalar a app com um toque.</p>
              <Button onClick={install} className="bg-primary text-black hover:bg-primary/90"><Download className="mr-1.5 h-4 w-4" />Instalar agora</Button>
            </>
          ) : (
            <ol className="space-y-2">
              <Step n="1">No Chrome do Android, abre o menu <b>⋮</b>.</Step>
              <Step n="2">Escolhe <b>Instalar aplicação</b> ou <b>Adicionar ao ecrã principal</b>.</Step>
              <Step n="3">No computador, aparece um ícone de instalação na barra de endereço.</Step>
            </ol>
          )}
        </div>

        <div className="rounded-xl border border-border p-4">
          <h3 className="mb-2 flex items-center gap-2 font-poppins text-sm font-semibold">
            {subscribed ? <Bell className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4 text-muted-foreground" />} Notificações
          </h3>
          {!supported ? (
            <p className="text-sm text-muted-foreground">Este browser não suporta notificações.</p>
          ) : ios && !installed ? (
            <p className="text-sm text-muted-foreground">
              No iPhone, as notificações só funcionam <b>depois de instalares a app</b> no ecrã principal (iOS 16.4 ou mais recente). Instala primeiro e volta aqui.
            </p>
          ) : (
            <>
              <p className="mb-3 text-sm text-muted-foreground">
                {subscribed
                  ? "Este dispositivo recebe os avisos do CRM mesmo com a app fechada: tarefas atribuídas, menções, faturas em atraso e renovações."
                  : "Liga para receberes tarefas atribuídas, menções, faturas em atraso e renovações, mesmo com a app fechada."}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={toggle} disabled={busy} variant={subscribed ? "outline" : "default"} className={subscribed ? "" : "bg-primary text-black hover:bg-primary/90"}>
                  {subscribed ? <><BellOff className="mr-1.5 h-4 w-4" />Desligar</> : <><Bell className="mr-1.5 h-4 w-4" />Ligar notificações</>}
                </Button>
                {subscribed && <Button variant="outline" onClick={test}>Enviar teste</Button>}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">Cada dispositivo liga-se separadamente — telemóvel e computador são independentes.</p>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
